/**
 * ComparisonTab — sheet "Comparison": so sánh current PO với comparison PO
 * (spec §6 + `plan/production/BATCH_REPORT_COMPARISON_DATA_SPEC.md`).
 *
 * Data 100% LIVE từ `GET /factories/{fc}/batch-report/{po}/comparison?comparison_po_no=`
 * (aggregation 2 view + reuse Production Analysis). KHÔNG mock/dummy
 * ([[feedback_api_view_source_of_truth]]): chưa chọn PO/comparison → empty-state;
 * loading → spinner; lỗi → Alert.
 *
 * Style BOM (block accent bar + số kế toán NumText/PctText) + bảng dữ liệu theo
 * skill `/table-style` đầy đủ: FilterBar Search global + per-column filter ▽ + sort
 * click header + resize width (kéo handle) + wrap-on-resize auto-height (§7.8).
 * Component dùng chung `FilterTable` cho cả 3 bảng.
 *
 * Sections (đúng workbook):
 *   ① Yield comparison by item (prev vs cur %, variance) + dòng TOTAL.
 *   ② FAQ quality comparison (12 chỉ tiêu input-side, 3 dòng prev/cur/Variance).
 *   ③ Narrative quality notes — số weighted-avg/item + Ghi chú/Qui chuẩn.
 *   ④ Price info (LIFFE/Diff + Premium vs G2 theo item) + ⑤ Production info.
 */
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  Stack,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { type ReactNode } from 'react'

import {
  useGetBatchReportComparisonQuery,
  type BatchReportComparison,
  type FaqQualityRow,
} from '../../../../store/api/factoryBomApi'
import { fmtMt, fmtPlain, fmtUsd, fmtVarPct, formatAccessDate } from './formatters'
import { BlockTitle, NumText, PctText } from './reportUi'
import {
  COLORS,
  neutralChipSx,
  sectionCardSx,
  sectionHeaderSx,
  sectionSubtitleSx,
  sectionTitleSx,
  varianceColor,
} from './styleTokens'
import { FilterTable, type FilterCol } from './FilterTable'

interface Props {
  factoryCode: string
  poNo: string
  /** PO list (Date filter chung) để chọn comparison PO. */
  poOptions: string[]
  poMeta: Record<string, { bomCode: string; poDate: string }>
  /** Comparison PO (controlled — lift lên BatchReportPage để Export dùng chung). */
  cmpPo: string
  onCmpPoChange: (po: string) => void
}


const totalCellSx = {
  fontWeight: 700,
  whiteSpace: 'nowrap',
  bgcolor: COLORS.bgSubtle,
  borderTop: `1px solid ${COLORS.borderStrong}`,
  borderBottom: 'none',
} as const

/** Section header kiểu BOM: BlockTitle (accent bar) trái + caption/right phải. */
function SectionHead({ title, caption, right }: { title: string; caption?: string; right?: ReactNode }) {
  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap sx={{ gap: 1 }}>
      <BlockTitle>{title}</BlockTitle>
      <Stack direction="row" alignItems="center" spacing={1.5} useFlexGap flexWrap="wrap" sx={{ justifyContent: 'flex-end' }}>
        {caption ? <Typography sx={{ ...sectionSubtitleSx, mb: 0 }}>{caption}</Typography> : null}
        {right}
      </Stack>
    </Stack>
  )
}

/* ---------- Section ①: Yield comparison by item ------------------------- */

type YieldRow = BatchReportComparison['yieldComparison'][number]

/**
 * Diverging data bar cho cột Variance (Excel col H = in-cell chart sau Var):
 * thanh căn giữa trục 0 — Var dương → bar xanh sang phải, âm → đỏ sang trái.
 * Scale RESPONSIVE theo data: |Var| lớn nhất bảng (`maxAbs`) đầy nửa ô, item khác
 * scale theo → không cần mốc cứng, item lệch nhất luôn full.
 */
function VarianceBar({ value, maxAbs }: { value: number; maxAbs: number }) {
  const half = maxAbs > 0 ? Math.min(50, (Math.abs(value) / maxAbs) * 50) : 0
  const positive = value > 0
  const color = positive ? COLORS.success : COLORS.danger
  return (
    <Box sx={{ position: 'relative', height: 26, width: '100%', minWidth: 22, display: 'flex', alignItems: 'center' }}>
      {/* trục 0 — vạch mảnh dịu */}
      <Box
        sx={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          width: '1px',
          height: 22,
          bgcolor: COLORS.border,
        }}
      />
      {/* thanh phẳng mỏng, màu đặc, hình chữ nhật vuông góc, mọc từ trục */}
      {value !== 0 && half > 0 ? (
        <Box
          sx={{
            position: 'absolute',
            height: 13,
            borderRadius: 0,
            bgcolor: color,
            width: `${half}%`,
            ...(positive ? { left: '50%' } : { right: '50%' }),
          }}
        />
      ) : null}
    </Box>
  )
}

function YieldComparison({ rows, curPo, cmpPo, headerRight }: { rows: YieldRow[]; curPo: string; cmpPo: string; headerRight?: ReactNode }) {
  // Scale responsive: |Var| lớn nhất bảng → bar item lệch nhất đầy nửa ô.
  const maxAbsVar = rows.reduce((m, r) => Math.max(m, Math.abs(r.variance || 0)), 0)
  const cols: FilterCol<YieldRow>[] = [
    { key: 'item', label: 'Item', width: 110, frozen: true, value: (r) => r.item },
    { key: 'category', label: 'Category', width: 120, filter: 'checkboxes', value: (r) => r.category },
    { key: 'premium', label: 'Premium', width: 96, align: 'right', numeric: true, value: (r) => r.premium, render: (r) => <NumText n={r.premium} d={2} usd /> },
    // Title 2 cột = PO No: cột "previous" = Compare-with PO, "current" = PO đang chọn.
    // Đổi theo PO/Compare-with; width đủ rộng để hiện full PO No.
    { key: 'previous', label: cmpPo || '—', width: 150, align: 'right', numeric: true, value: (r) => r.previous, render: (r) => <PctText frac={r.previous} d={2} /> },
    { key: 'current', label: curPo || '—', width: 150, align: 'right', numeric: true, value: (r) => r.current, render: (r) => <PctText frac={r.current} d={2} /> },
    {
      key: 'variance',
      label: 'Var.',
      width: 100,
      align: 'right',
      numeric: true,
      value: (r) => r.variance,
      render: (r) => (
        <Box component="span" sx={{ color: varianceColor(r.variance), fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
          {fmtVarPct(r.variance, 2)}
        </Box>
      ),
    },
    // Cột chart (Excel col H): diverging data bar trực quan hoá Variance. Sort theo
    // Var (numeric) nhưng KHÔNG filter (filter: 'none').
    {
      key: 'varChart',
      label: 'Chart',
      width: 40,
      numeric: true,
      filter: 'none',
      value: (r) => r.variance,
      render: (r) => <VarianceBar value={r.variance} maxAbs={maxAbsVar} />,
    },
  ]
  return (
    <Box>
      <SectionHead title="Batch out-turn comparison" right={headerRight} />
      <FilterTable
        storageKey="comparison.yield"
        columns={cols}
        rows={rows}
        rowKey={(r) => r.item}
        searchPlaceholder="Search items…"
        footer={(drows) => {
          const t = drows.reduce(
            (a, r) => ({ previous: a.previous + r.previous, current: a.current + r.current, variance: a.variance + r.variance }),
            { previous: 0, current: 0, variance: 0 },
          )
          return (
            <TableRow>
              <TableCell sx={totalCellSx}>TOTAL</TableCell>
              <TableCell sx={totalCellSx} />
              <TableCell sx={totalCellSx} />
              <TableCell align="right" sx={totalCellSx}><PctText frac={t.previous} d={2} /></TableCell>
              <TableCell align="right" sx={totalCellSx}><PctText frac={t.current} d={2} /></TableCell>
              <TableCell align="right" sx={{ ...totalCellSx, color: varianceColor(t.variance), fontVariantNumeric: 'tabular-nums' }}>
                {fmtVarPct(t.variance, 2)}
              </TableCell>
              <TableCell sx={totalCellSx} />
            </TableRow>
          )
        }}
      />
    </Box>
  )
}

/* ---------- Section ②: FAQ quality comparison --------------------------- */

function FaqQualityCompare({ rows }: { rows: FaqQualityRow[] }) {
  const qcols: { key: keyof FaqQualityRow; label: string }[] = [
    { key: 'mc', label: 'MC' },
    { key: 'blacks', label: 'Blacks' },
    { key: 'brokens', label: 'Brokens' },
    { key: 'bb', label: 'BB' },
    { key: 'fm', label: 'FM' },
    { key: 'mouldy', label: 'Mouldy' },
    { key: 'brown', label: 'Brown' },
    { key: 'partlyBlack', label: 'Partly Black' },
    { key: 'immature', label: 'Immature' },
    { key: 's18', label: 'S18' },
    { key: 's16', label: 'S16' },
    { key: 's13', label: 'S13' },
  ]
  const cols: FilterCol<FaqQualityRow>[] = [
    {
      key: 'po',
      label: 'PO',
      width: 180,
      frozen: true,
      value: (r) => r.po,
      render: (r) => (
        <Box component="span" className="app-table-cell-wrap" sx={{ fontWeight: r.po === 'Variance' ? 700 : 600 }}>
          {r.po}
        </Box>
      ),
    },
    ...qcols.map<FilterCol<FaqQualityRow>>((c) => ({
      key: String(c.key),
      label: c.label,
      width: 92,
      align: 'right',
      numeric: true,
      value: (r) => Number(r[c.key]) || 0,
      render: (r) => {
        const v = Number(r[c.key]) || 0
        return r.po === 'Variance' ? (
          <Box component="span" sx={{ color: varianceColor(v), fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
            {(v >= 0 ? '+' : '') + fmtPlain(v, 2)}
          </Box>
        ) : (
          <NumText n={v} d={2} />
        )
      },
    })),
  ]
  return (
    <Box>
      <SectionHead title="FAQ quality comparison" />
      <FilterTable storageKey="comparison.faq" columns={cols} rows={rows} rowKey={(r) => r.po} searchPlaceholder="Search…" />
    </Box>
  )
}

/* ---------- Section ③: Finished product quality (số/item) --------------- */

type NarrativeRow = BatchReportComparison['narrative'][number]

/** Quality weighted-avg/item của thành phẩm Graded (MC/BB/FM/S18/S16/S13). Note &
 *  Standard đã chuyển sang tab Production Analysis — block này chỉ hiện số (read-only). */
function FinishedQuality({ rows }: { rows: NarrativeRow[] }) {
  const cols: FilterCol<NarrativeRow>[] = [
    {
      key: 'item',
      label: 'Item',
      width: 110,
      frozen: true,
      value: (r) => r.item,
      render: (r) => <Box component="span" className="app-table-cell-wrap" sx={{ fontWeight: 600 }}>{r.item}</Box>,
    },
    { key: 'mc', label: 'MC', width: 80, align: 'right', numeric: true, value: (r) => r.mc, render: (r) => <NumText n={r.mc} d={2} /> },
    { key: 'bb', label: 'BB', width: 80, align: 'right', numeric: true, value: (r) => r.bb, render: (r) => <NumText n={r.bb} d={2} /> },
    { key: 'fm', label: 'FM', width: 80, align: 'right', numeric: true, value: (r) => r.fm, render: (r) => <NumText n={r.fm} d={2} /> },
    { key: 's18', label: 'S18', width: 80, align: 'right', numeric: true, value: (r) => r.s18, render: (r) => <NumText n={r.s18} d={2} /> },
    { key: 's16', label: 'S16', width: 80, align: 'right', numeric: true, value: (r) => r.s16, render: (r) => <NumText n={r.s16} d={2} /> },
    { key: 's13', label: 'S13', width: 80, align: 'right', numeric: true, value: (r) => r.s13, render: (r) => <NumText n={r.s13} d={2} /> },
  ]
  return (
    <Box>
      <SectionHead
        title="Finished product quality after processing"
        caption="Graded items from the batch out-turn comparison."
      />
      <FilterTable storageKey="comparison.narrative" columns={cols} rows={rows} rowKey={(r) => r.item} searchPlaceholder="Search items…" />
    </Box>
  )
}

/* ---------- Section ④/⑤: Price info + Production info -------------------- */

// Cùng format Cost Breakdown (BomTab): label nhỏ trái / value đậm phải, nowrap.
const PP_LABEL_FS = '0.72rem'
const PP_VALUE_FS = '0.7rem'

function PriceProductionPanels({
  price,
  prod,
}: { price: BatchReportComparison['priceInfo']; prod: BatchReportComparison['productionInfo'] }) {
  return (
    <Box>
      <BlockTitle>Price &amp; production info</BlockTitle>
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="stretch">
        {/* ④ Price info */}
        <Box sx={{ ...sectionCardSx, flex: '1 1 320px', minWidth: 280 }}>
          <Box sx={{ ...sectionHeaderSx, px: 1.25 }}>
            <Typography sx={{ ...sectionTitleSx, fontSize: '0.78rem', whiteSpace: 'nowrap' }}>Price info</Typography>
            <Chip size="small" label="USD" sx={neutralChipSx} />
          </Box>
          <Box sx={{ p: 1.5 }}>
            <PpRow label="LIFFE" value={price.liffe == null ? '—' : fmtUsd(price.liffe)} />
            <PpRow label="Diff" value={price.diffLabel || (price.g2Diffs ? fmtUsd(price.g2Diffs) : '—')} />
            {price.priceDate ? <PpRow label="Price date" value={formatAccessDate(price.priceDate)} /> : null}
            <PpGroupLabel>Premium vs G2 (graded)</PpGroupLabel>
            {price.premiumByItem.length === 0 ? (
              <Typography sx={{ fontSize: PP_LABEL_FS, color: COLORS.textMuted, mt: 0.5 }}>—</Typography>
            ) : (
              price.premiumByItem.map((p) => (
                <PpRow key={p.item} label={p.item} value={fmtUsd(p.premium)} />
              ))
            )}
          </Box>
        </Box>
        {/* ⑤ Production info */}
        <Box sx={{ ...sectionCardSx, flex: '1 1 320px', minWidth: 280 }}>
          <Box sx={{ ...sectionHeaderSx, px: 1.25 }}>
            <Typography sx={{ ...sectionTitleSx, fontSize: '0.78rem', whiteSpace: 'nowrap' }}>Production info</Typography>
            <Chip size="small" label="Mt / %" sx={neutralChipSx} />
          </Box>
          <Box sx={{ p: 1.5 }}>
            <PpRow label="Total input" value={`${fmtMt(prod.totalInput)} Mt`} />
            {/* Dòng kết quả nhấn (border-top) — mirror ResultRow của Cost Breakdown. */}
            <PpResultRow label="Total out-turn" value={`${fmtMt(prod.totalOutturnMt)} Mt`} tone="success" />
            <PpGroupLabel>Loss breakdown</PpGroupLabel>
            {/* Số có dấu (+/−) theo chuẩn fmtVarPct (giống bảng yield/FAQ); âm = giảm = xanh. */}
            <PpRow label="Loss" value={fmtVarPct(prod.loss, 2)} tone={prod.loss < 0 ? 'success' : 'danger'} />
            <PpRow label="MC loss" value={fmtVarPct(prod.mcLoss, 2)} tone={prod.mcLoss < 0 ? 'success' : 'danger'} />
            <PpRow label="Unexpl. loss" value={fmtVarPct(prod.unexplainedLoss, 2)} tone={prod.unexplainedLoss < 0 ? 'success' : 'danger'} />
          </Box>
        </Box>
      </Stack>
    </Box>
  )
}

/* Dòng label/value — mirror CostResult của BomTab. */
function PpRow({ label, value, tone }: { label: string; value: string; tone?: 'danger' | 'success' }) {
  const color = tone === 'danger' ? COLORS.danger : tone === 'success' ? COLORS.success : COLORS.text
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mt: 0.5 }}>
      <Typography sx={{ fontSize: PP_LABEL_FS, color: COLORS.textMuted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', mr: 1 }}>{label}</Typography>
      <Typography sx={{ fontSize: PP_VALUE_FS, fontWeight: 800, color, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
    </Stack>
  )
}

/* Dòng kết quả nhấn — mirror ResultRow của BomTab (border-top + đậm). */
function PpResultRow({ label, value, tone }: { label: string; value: string; tone?: 'danger' | 'success' }) {
  const color = tone === 'danger' ? COLORS.danger : tone === 'success' ? COLORS.success : COLORS.text
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="baseline"
      sx={{ mt: 0.75, pt: 0.75, borderTop: `1px solid ${COLORS.borderStrong}` }}
    >
      <Typography sx={{ fontSize: PP_VALUE_FS, fontWeight: 800, color: COLORS.text, whiteSpace: 'nowrap' }}>{label}</Typography>
      <Typography sx={{ fontSize: PP_VALUE_FS, fontWeight: 800, color, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
    </Stack>
  )
}

function PpGroupLabel({ children }: { children: React.ReactNode }) {
  return (
    <Typography sx={{ fontSize: '0.66rem', color: COLORS.textSubtle, mt: 1.5, mb: 0.25, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>
      {children}
    </Typography>
  )
}

/* ---------- comparison PO selector -------------------------------------- */

function ComparisonPoPicker({
  options,
  poMeta,
  value,
  onChange,
}: {
  options: string[]
  poMeta: Props['poMeta']
  value: string
  onChange: (v: string) => void
}) {
  return (
    <Stack direction="row" spacing={0.75} alignItems="center" useFlexGap flexWrap="wrap">
      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: 0.4, whiteSpace: 'nowrap' }}>
        Compare with
      </Typography>
      <Autocomplete
        size="small"
        options={options}
        value={value || null}
        onChange={(_e, v) => onChange(v ?? '')}
        getOptionLabel={(o) => o || ''}
        isOptionEqualToValue={(a, b) => a === b}
        renderOption={(props, option) => {
          const m = poMeta[option]
          const { key, ...optionProps } = props as typeof props & { key?: string | number }
          return (
            <Box component="li" key={key} {...optionProps} sx={{ display: 'block !important', py: 0.4 }}>
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: COLORS.text, lineHeight: 1.2 }}>
                {option}
              </Typography>
              {m && (m.bomCode || m.poDate) ? (
                <Typography sx={{ fontSize: '0.66rem', color: COLORS.textMuted, lineHeight: 1.15 }}>
                  {[m.bomCode, m.poDate].filter(Boolean).join(' · ')}
                </Typography>
              ) : null}
            </Box>
          )
        }}
        slotProps={{ paper: { sx: { minWidth: 200, '& .MuiAutocomplete-option': { minHeight: 'auto' } } } }}
        sx={{
          width: { xs: 168, sm: 184 },
          // Compact nhưng KHÔNG override `endAdornment top` (để MUI tự căn giữa nút mũi
          // tên, tránh lệch). py nhỏ + icon padding nhỏ; giữ 16px ở xs chống iOS zoom.
          '& .MuiOutlinedInput-root': { py: 0.25 },
          '& .MuiInputBase-input': { fontSize: { xs: '16px', sm: '0.72rem' } },
          '& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator': { p: 0.25 },
        }}
        renderInput={(params) => <TextField {...params} placeholder="Comparison PO…" />}
      />
    </Stack>
  )
}

/* ---------- main tab ----------------------------------------------------- */

export default function ComparisonTab({ factoryCode, poNo, poOptions, poMeta, cmpPo, onCmpPoChange }: Props) {
  // Comparison PO controlled từ BatchReportPage. Mặc định (PO đầu khác current)
  // được hydrate ở parent — tab chỉ đọc/đổi qua onCmpPoChange.
  const setCmpPo = onCmpPoChange

  const { data, isFetching, isError } = useGetBatchReportComparisonQuery(
    { factoryCode, poNo, comparisonPoNo: cmpPo },
    { skip: !poNo },
  )

  // Picker (+ refreshing) — ở state data đặt CÙNG DÒNG title block "So sánh thu hồi
  // của mẻ" (tiết kiệm không gian); loading/error bọc row căn phải.
  const pickerEl = (
    <Stack direction="row" alignItems="center" spacing={1} useFlexGap flexWrap="wrap" sx={{ justifyContent: 'flex-end' }}>
      {isFetching ? (
        <Typography sx={{ fontSize: '0.72rem', color: COLORS.textMuted }}>refreshing…</Typography>
      ) : null}
      <ComparisonPoPicker options={poOptions.filter((p) => p !== poNo)} poMeta={poMeta} value={cmpPo} onChange={setCmpPo} />
    </Stack>
  )

  if (!poNo) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
        <Typography sx={{ fontSize: '0.85rem', color: COLORS.textMuted }}>
          Select a PO to view the comparison.
        </Typography>
      </Box>
    )
  }
  if (isError) {
    return (
      <Stack spacing={2}>
        {pickerEl}
        <Alert severity="error" sx={{ m: 1 }}>
          Could not load comparison data for {poNo}. Please retry or check the system connection.
        </Alert>
      </Stack>
    )
  }
  if (!data) {
    return (
      <Stack spacing={2}>
        {pickerEl}
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
          <CircularProgress size={22} />
        </Box>
      </Stack>
    )
  }

  return (
    <Stack spacing={2}>
      <YieldComparison rows={data.yieldComparison} curPo={poNo} cmpPo={data.comparisonPoNo} headerRight={pickerEl} />
      <FaqQualityCompare rows={data.faqQuality} />
      <FinishedQuality rows={data.narrative} />
      <PriceProductionPanels price={data.priceInfo} prod={data.productionInfo} />
    </Stack>
  )
}
